import React, { useEffect, useRef, useState } from 'react';
const getInitialHostAuthState = () => {
  if (typeof window === 'undefined') return undefined;
  return window.__AUTH_STATE__;
};

const AUTH_CALLBACK_QUERY_KEYS = new Set(['state', 'expires_at']);

const stripAuthCallbackParams = (search = '') => {
  if (!search) return '';

  const params = new URLSearchParams(search);
  let changed = false;

  AUTH_CALLBACK_QUERY_KEYS.forEach((key) => {
    if (!params.has(key)) return;
    params.delete(key);
    changed = true;
  });

  if (!changed) return search;

  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const buildReturnUrl = () => {
  if (typeof window === 'undefined') return '';

  return (
    window.location.pathname +
    stripAuthCallbackParams(window.location.search) +
    window.location.hash
  );
};
/**
 * AuthenticationGate
 *
 * - Reads initial auth state from `window.__AUTH_STATE__` if the host has set it.
 * - Listens for host `auth-state-change` events.
 * - Otherwise calls `authClient.ensureSession()` once to determine state.
 * - When unauthenticated, requests a login (host event in single-spa, or
 *   direct `authClient.login` fallback).
 */
export const AuthenticationGate = ({
  authClient,
  postLoginRedirectUriOverride = '',
  options,
  renderAuthenticated,
  children,
  loadingFallback = null,
  unauthenticatedFallback = null,
  onAuthenticated,
  enableHostAuthEvents = true,
}) => {
  if (!authClient) {
    throw new Error('AuthenticationGate requires an authClient');
  }
  const { ensureSession, login } = authClient;
  const initialGlobal = getInitialHostAuthState();
  const [authState, setAuthState] = useState(() => {
    if (initialGlobal?.authChecked) {
      return {
        authenticated: !!initialGlobal.authenticated,
        loading: false,
        authChecked: true,
      };
    }
    return { authenticated: false, loading: true, authChecked: false };
  });
  const loginRequestedRef = useRef(false);
  const requestLogin = () => {
    if (typeof window === 'undefined' || loginRequestedRef.current) return;
    loginRequestedRef.current = true;
    const detail = {
      returnUrl: buildReturnUrl(),
      postLoginRedirectUriOverride,
    };
    const hasSingleSpa = !!(
      window.singleSpa && typeof window.singleSpa.navigateToUrl === 'function'
    );
    if (hasSingleSpa) {
      window.dispatchEvent(new CustomEvent('auth-request-login', { detail }));
      return;
    }
    Promise.resolve(login(postLoginRedirectUriOverride || undefined, detail.returnUrl))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Local login execution failed:', e);
        loginRequestedRef.current = false;
      });
  };
  // React to global auth-state changes from the host shell.
  useEffect(() => {
    if (!enableHostAuthEvents || typeof window === 'undefined') return undefined;
    const handleAuthChange = (event) => {
      const next = {
        authenticated: !!event.detail?.authenticated,
        loading: !!event.detail?.loading,
        authChecked: !!event.detail?.authChecked,
      };
      setAuthState(next);
      if (next.authChecked && !next.authenticated) requestLogin();
    };
    window.addEventListener('auth-state-change', handleAuthChange);
    if (initialGlobal?.authChecked && !initialGlobal?.authenticated) requestLogin();
    return () => window.removeEventListener('auth-state-change', handleAuthChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postLoginRedirectUriOverride, enableHostAuthEvents]);
  // Local fallback: if the host hasn't already published an auth state,
  // run a single shared `ensureSession` call.
  useEffect(() => {
    if (authState.authChecked) return;
    if (typeof ensureSession !== 'function') return;
    let cancelled = false;
    ensureSession()
      .then((session) => {
        if (cancelled) return;
        const authenticated = !!session?.authenticated;
        setAuthState({ authenticated, loading: false, authChecked: true });
        if (!authenticated) requestLogin();
      })
      .catch(() => {
        if (cancelled) return;
        setAuthState({ authenticated: false, loading: false, authChecked: true });
        requestLogin();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.authChecked]);
  // Notify consumer when authenticated.
  useEffect(() => {
    if (!authState.authenticated || typeof onAuthenticated !== 'function') return;
    onAuthenticated({ authState, options });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.authenticated]);
  if (authState.loading || !authState.authChecked) return loadingFallback;
  if (!authState.authenticated) return unauthenticatedFallback;
  if (typeof renderAuthenticated === 'function') {
    return renderAuthenticated({ options, authState });
  }
  if (typeof children === 'function') {
    return children({ options, authState });
  }
  return children || null;
};
